"use client";

import { PropertyCard } from "@/components/app/properties/property-card";
import { PropertySheet } from "@/components/app/properties/property-sheet";
import { useJourneyData } from "@/hooks/use-journey-data";
import { useMarketMatches } from "@/hooks/use-market-matches";
import type { Property, SavedProperty } from "@/schemas/property";
import { AnimatePresence, motion } from "framer-motion";
import { HomeIcon, SparklesIcon } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { EmptyResults } from "../empty-results";
import { PropertyLimitIndicator } from "./property-limit-indicator";

interface PropertyGridProps {
  variant?: "default" | "compact";
  savedItems?: boolean;
}

export function PropertyGrid({
  variant = "default",
  savedItems,
}: PropertyGridProps) {
  const { removeProperty, savedProperties } = useJourneyData();
  const { data: marketMatches } = useMarketMatches();
  const [selectedProperty, setSelectedProperty] = useState<
    Property | SavedProperty | null
  >(null);

  const [validMarketMatches, setValidMarketMatches] = useState<Property[]>([]);

  useEffect(() => {
    if (!marketMatches?.data) return;

    const savedIds = new Set(
      savedProperties?.map((saved) => saved.property_id).filter(Boolean) ?? [],
    );
    const filteredMatches = marketMatches.data.filter(
      (item) => item.id && !savedIds.has(item.id),
    ) as Property[];

    setValidMarketMatches(filteredMatches);
  }, [savedProperties, marketMatches]);

  return (
    <>
      <PropertyLimitIndicator currentCount={savedProperties.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <AnimatePresence>
          {savedItems ? (
            savedProperties.length > 0 ? (
              savedProperties.map((item: Property | SavedProperty) => {
                const isSavedProperty = (
                  item: Property | SavedProperty,
                ): item is SavedProperty =>
                  "property_id" in item && "property" in item;

                const isOptimisticData = item.id?.startsWith("temp-");
                if (isOptimisticData) {
                  return <Fragment key={item.id} />;
                }

                const savedProperty = isSavedProperty(item) ? item : null;
                const propertyData =
                  savedProperty?.property || (item as Property);

                if (savedProperty && !propertyData) return null;

                return (
                  <motion.div
                    key={savedProperty?.id ?? propertyData.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                  >
                    <PropertyCard
                      property={propertyData}
                      savedPropertyId={savedProperty?.id}
                      variant={variant}
                      onRemove={
                        savedProperty
                          ? () => removeProperty.mutate(savedProperty.id!)
                          : undefined
                      }
                      onClick={() => setSelectedProperty(item)}
                    />
                  </motion.div>
                );
              })
            ) : (
              <div className="col-span-full">
                <div className="bg-gray-50/50 md:w-1/2 mx-auto backdrop-blur-sm rounded-3xl p-12 border border-gray-100 shadow-sm">
                  <div className="space-y-4">
                    <div className="bg-white rounded-full p-4 w-16 h-16 mx-auto flex items-center justify-center shadow-sm ring-1 ring-gray-100">
                      <HomeIcon
                        className="w-8 h-8 text-primary"
                        strokeWidth={1.5}
                      />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-2xl font-semibold text-gray-900 text-center tracking-tight">
                        No saved properties yet
                      </h3>
                      <p className="text-gray-500 max-w-md mx-auto text-center">
                        When you find properties you like, save them here to
                        keep track of your favorites.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : validMarketMatches.length > 0 ? (
            validMarketMatches.map((item: Property | SavedProperty) => {
              const isSavedProperty = (
                item: Property | SavedProperty,
              ): item is SavedProperty =>
                "property_id" in item && "property" in item;

              const savedProperty = isSavedProperty(item) ? item : null;
              const propertyData =
                savedProperty?.property || (item as Property);

              if (savedProperty && !propertyData) return null;

              return (
                <motion.div
                  key={savedProperty?.id ?? propertyData.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <PropertyCard
                    property={propertyData}
                    savedPropertyId={savedProperty?.id}
                    variant={variant}
                    onRemove={
                      savedProperty
                        ? () => removeProperty.mutate(savedProperty.id!)
                        : undefined
                    }
                    onClick={() => setSelectedProperty(item)}
                  />
                </motion.div>
              );
            })
          ) : (
            <EmptyResults
              icon={<SparklesIcon className="w-8 h-8 text-primary" />}
              title="No matching properties found"
              description="Try adjusting your search filters to find more properties that match your criteria."
            />
          )}
        </AnimatePresence>
      </div>

      <PropertySheet
        property={
          selectedProperty
            ? "property" in selectedProperty
              ? selectedProperty.property
              : selectedProperty
            : null
        }
        savedPropertyId={
          selectedProperty
            ? "property_id" in selectedProperty
              ? selectedProperty.id
              : undefined
            : undefined
        }
        onClose={() => setSelectedProperty(null)}
      />
    </>
  );
}
